import { useGetMenuFormPermissions } from '@/api/permission';
import { useToast } from '@/hooks/use-toast';
import { mainMenuItems } from '@/utils/constant';
import { MenuFormPermission } from '@/utils/types/permission';
import { useEffect, useState } from 'react';

interface MainMenuItem {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
  module: string;
  key: string;
}

interface UseMainMenuPermissionsReturn {
  menuItems: Record<string, MainMenuItem>;
  isLoading: boolean;
  error: Error | null;
  canView: (key: string) => boolean;
}

const useMainMenuPermissions = (): UseMainMenuPermissionsReturn => {
  const [filteredMenu, setFilteredMenu] = useState<Record<string, MainMenuItem>>({});
  const [allowedKeys, setAllowedKeys] = useState<Set<string>>(new Set());
  const { isLoading, data, error } = useGetMenuFormPermissions();
  const { toast } = useToast();

  const filterMenuByPermissions = (
    menu: Record<string, MainMenuItem>,
    permissionsData: MenuFormPermission[],
  ): Record<string, MainMenuItem> => {
    const allowed: Record<string, MainMenuItem> = {};
    const keys = new Set<string>();

    permissionsData.forEach((permission) => {
      if (permission.isView) {
        keys.add(permission.dbFormName);
      }
    });

    Object.entries(menu).forEach(([key, item]) => {
      if (!item.key || keys.has(item.key)) {
        allowed[key] = item;
      }
    });

    setAllowedKeys(keys);
    return allowed;
  };

  const canView = (key: string): boolean => {
    if (!key || key === '') return true;
    return allowedKeys.has(key);
  };

  useEffect(() => {
    const showAllMenusAsFallback = () => {
      setAllowedKeys(new Set());
      setFilteredMenu(mainMenuItems);
    };

    const hideAllMenus = () => {
      setAllowedKeys(new Set());
      setFilteredMenu({});
    };

    if (error) {
      console.error('Failed to fetch main menu permissions, showing all menu items as fallback');
      showAllMenusAsFallback();
      return;
    }

    if (data?.data && data?.data?.length > 0) {
      const filtered = filterMenuByPermissions(mainMenuItems, data.data);
      setFilteredMenu(filtered);
    } else if (data && data.status === true && Array.isArray(data.data)) {
      hideAllMenus();
    } else if (!data) {
      hideAllMenus();
    } else {
      showAllMenusAsFallback();
    }
  }, [data, error]);

  useEffect(() => {
    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch main menu permissions',
        variant: 'destructive',
      });
    }
  }, [error]);

  return {
    menuItems: filteredMenu,
    isLoading,
    error,
    canView,
  };
};

export default useMainMenuPermissions;
